import { Box, Button, Card, Checkbox, FormControl, Grow, IconButton, InputAdornment, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, Stack, TextField, Typography, useMediaQuery, FormControlLabel } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useMobileApi } from '../../../Api/Mobile/useMobileApi';
import { AuthMobileEmployeeRequest, CompleteRegistrationRequest, MobileAuthVovClient, PartialRegistrationRequest } from '../../../ApiOld/Api/ApiServerVov';
import { useEmployeeContext } from '../../../Contexts/Providers/EmployeeLayoutProvider';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import LanguagePicker from '../../Shared/LanguagePicker';
import { Theme } from '@mui/material/styles';
import StavarioLogoBackground from '../../mobile-app/Shared/StavarioLogoBackground';
import { useLocation, useNavigate } from 'react-router';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { enqueueSnackbar } from 'notistack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CoPresentIcon from '@mui/icons-material/CoPresent';
import NumericPasswordBox from '../../mobile-app/Shared/NumericPasswordBox';

export interface IMobileLoginProps {
}

export function MobileLogin(props: IMobileLoginProps) {
    const { t, i18n } = useTranslation();
    const { login } = useEmployeeContext();
    const { diagnosticAlerts, platform } = useEmployeeContext();
    const { unauthorizedApi } = useMobileApi(MobileAuthVovClient)
    const location = useLocation();
    const navigate = useNavigate();
    const [domain, setDomain] = useState('');
    const [code, setCode] = useState('');
    const [pin, setPin] = useState('');
    const [messagingToken, setMessagingToken] = useState<string | undefined>(undefined);
    const [editDomain, setEditDomain] = useState(true);
    const biggerThanXs = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const [isIOS, setIsIOS] = useState(true);
    const [currentStep, setCurrentStep] = useState<'Sign' | 'Welcome' | 'Partial' | 'Complete' | 'Created'>('Sign');
    const [companyName, setCompanyName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [pendingRegistrationId, setPendingRegistrationId] = useState<number>();
    const [companyNumber, setCompanyNumber] = useState('');
    const [selectedModules, setSelectedModules] = useState<string[]>([]);
    const [rememberCredentials, setRememberCredentials] = useState(false);

    window.fillCredentials = (code: string, pin: string) => {
        setCode(code);
        setPin(pin);
    }

    useEffect(() => {
        //@ts-ignore
        if (window.ReactNativeWebView) {
            //@ts-ignore
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'translate-prompt',
                data: JSON.stringify({ title: t('VerifyItsYou'), type: 'Verify' }),
            }));
        }
    }, [t, i18n.language]);

    useEffect(() => {
        //@ts-ignore
        if (window.ReactNativeWebView && currentStep === "Sign") {
            //@ts-ignore
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'prompt-fill-credentials',
            }));
        }
    }, [currentStep]);

    useEffect(() => {
        const domain = localStorage.getItem('mobile_domain');
        setDomain(domain ? domain : '');
        if (domain) {
            setEditDomain(false);
        }
        const remember = localStorage.getItem('remember_credentials');
        setRememberCredentials(remember === 'true');
    }, []);

    useEffect(() => {
        if (!messagingToken) {
            //@ts-ignore
            if (window.ReactNativeWebView) {
                //@ts-ignore
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: 'request-fcm-token',
                }));
            }
        }
    }, [messagingToken]);

    window.sendFcmToken = (token: string) => {
        if (diagnosticAlerts)
            enqueueSnackbar('FCM token: ' + token, { variant: 'info' });
        setMessagingToken(token);
    }

    useEffect(() => {
        setIsIOS(platform === 'ios');
    }, [platform]);

    const handleLogin = useCallback(() => {
        unauthorizedApi.mobileLogin(new AuthMobileEmployeeRequest({
            domain: domain,
            code: code,
            pin: pin,
            messaginToken: messagingToken
        })).then((response) => {
            if (response.message) {
                enqueueSnackbar(t(response.message), { variant: 'error' });
            } else if (response.mobileToken) {
                localStorage.setItem('mobile_bearer', response.mobileToken);

                //@ts-ignore
                if (window.ReactNativeWebView) {
                    //@ts-ignore
                    window.ReactNativeWebView.postMessage(JSON.stringify({
                        type: 'save-credentials',
                        data: JSON.stringify({ code: rememberCredentials ? code : '', pin: rememberCredentials ? pin : '' })
                    }));
                }
                login(response);
            }
        });
    }, [unauthorizedApi, domain, code, pin, messagingToken, t, rememberCredentials, login]);

    const handlePartialRegistration = useCallback(async () => {
        await unauthorizedApi.partialRegistration(new PartialRegistrationRequest({
            companyName: companyName,
            email: email,
            source: 'MA',
            language: i18n.language,
        })).then((response) => {
            if (response.id) {
                setPendingRegistrationId(response.id);
                setCurrentStep('Complete');
            }
            else {
                enqueueSnackbar(response.message, { variant: 'error' });
            }
        });
    }, [companyName, email, i18n.language, unauthorizedApi]);

    const handleCompleteRegistration = useCallback(async () => {
        const phoneWithoutCountryCode = phone.startsWith(countryCode) ? phone.slice(countryCode.length) : phone;

        await unauthorizedApi.completeRegistration(new CompleteRegistrationRequest({
            pendingRegistrationId: pendingRegistrationId,
            companyName: companyName,
            email: email,
            phonePrefix: countryCode,
            phoneNumber: phoneWithoutCountryCode,
            language: i18n.language,
            source: 'MA',
            ico: companyNumber,
            hasAttendanceModule: selectedModules.includes('Attendance'),
            hasDiaryModule: selectedModules.includes('Construction Diary'),
            hasTasksModule: selectedModules.includes('Tasks'),
            hasWealthModule: selectedModules.includes('Property'),
            hasWarehousesModule: selectedModules.includes('warehouses.warehouses'),
            hasGanttModule: selectedModules.includes('todo.ganttDiagram'),
            hasReportsModule: selectedModules.includes('menu.reports'),
            hasAIModule: selectedModules.includes('AI'),
        })).then((res) => {
            if (res.ok) {
                setCurrentStep('Created');
                setCompanyName('');
                setEmail('');
                setPhone('');
                setCompanyNumber('');
                setSelectedModules([]);
            } else {
                enqueueSnackbar(res.message, { variant: 'error' });
            }
        })
    }, [companyName, companyNumber, countryCode, email, i18n.language, pendingRegistrationId, phone, selectedModules, unauthorizedApi]);

    const handlePhoneChange = (value, country) => {
        setCountryCode(country.dialCode);
        setPhone(value);
    };

    const modules = [
        'Construction Diary',
        'Attendance',
        'Tasks',
        'Property',
        'warehouses.warehouses',
        'todo.ganttDiagram',
        'menu.reports',
        'AI'
    ]

    return (
        <Box sx={{ position: 'relative', width: '100vw', height: '100vh' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
                <StavarioLogoBackground />
            </Box>
            <Box display='flex' alignItems={'center'} justifyContent={'center'} height={'100vh'} width={'100%'}>
                <Card sx={{ width: !biggerThanXs ? '90%' : '400px', zIndex: 3, p: 4, pb: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', alignItems: 'center' }}>
                        {currentStep !== 'Created' && <img
                            width="200px"
                            height="54px"
                            src="https://stavariokluc.vimovsem.cz/images/login-logo-stavario.svg"
                            alt="stavario"
                            onDoubleClick={() => {
                                //@ts-ignore
                                if (window.ReactNativeWebView)
                                    //@ts-ignore
                                    window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'clear-cache' }));
                            }} />}
                        {currentStep === 'Sign' && <Box display='flex' alignItems={'center'} flexDirection='column' sx={{ width: '100%' }}>
                            <TextField
                                sx={{ mt: 3 }}
                                size='small'
                                fullWidth
                                label={t('YourPersonalCode')}
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                type='number'
                                inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                }} />
                            <NumericPasswordBox
                                onEnter={handleLogin}
                                sx={{ mt: 3, mb: 1 }}
                                size='small'
                                fullWidth
                                label={t('YourPIN')}
                                password={pin}
                                onPasswordChange={(e) => setPin(e)} />
                            <Box sx={{ minHeight: 40, width: '100%', borderBottom: '1px solid #e0e0e0', pb: 1, mt: 2 }}>
                                <Grow in={editDomain} timeout={500}>
                                    <Box sx={{ width: '100%', display: editDomain ? 'block' : 'none' }}>
                                        <TextField
                                            onBlur={() => {
                                                if (domain)
                                                    setEditDomain(false);
                                                localStorage.setItem('mobile_domain', typeof domain === 'string' ? domain : JSON.stringify(domain));
                                            }}
                                            name='CompanyDomain'
                                            size='small'
                                            fullWidth
                                            label={t('CompanyDomain')}
                                            value={domain}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    setEditDomain(false);
                                                    localStorage.setItem('mobile_domain', typeof domain === 'string' ? domain : JSON.stringify(domain));
                                                }
                                            }}
                                            onChange={(e) => {
                                                setDomain(e.target.value)
                                            }}
                                            sx={{ height: '40px' }}
                                            InputProps={{
                                                endAdornment: domain.length > 0 ? (
                                                    <InputAdornment position="end">
                                                        <CheckIcon color='success' onClick={() => {
                                                            setEditDomain(false);
                                                            localStorage.setItem('mobile_domain', typeof domain === 'string' ? domain : JSON.stringify(domain));
                                                        }} />
                                                    </InputAdornment>
                                                ) : null,
                                            }} />
                                    </Box>
                                </Grow>
                                <Grow in={!editDomain} timeout={500}>
                                    <Box
                                        onClick={() => {
                                            setEditDomain(true);
                                            setTimeout(() => {
                                                const input = document.querySelector('input[name="CompanyDomain"]');
                                                if (input) {
                                                    (input as HTMLInputElement).focus();
                                                }
                                            }, 100);
                                        }}
                                        sx={{
                                            width: '100%',
                                            display: !editDomain ? 'flex' : 'none',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '40px',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: 'black' }}>{t('CompanyDomain')}:</Typography>
                                        <Stack direction={'row'} sx={{ alignItems: 'center', marginLeft: 1 }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 'bold', padding: 0.5, borderRadius: '4px', background: domain.length > 0 ? '#FFC600' : 'transparent', color: 'black' }}>
                                                {domain}
                                            </Typography>
                                            <IconButton>
                                                <EditIcon sx={{ color: 'black', width: 24, height: 24 }} />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Grow>
                            </Box>
                            <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', width: '100%', pb: 2,
                                //@ts-ignore
                                pt: window.ReactNativeWebView ? undefined : 2
                            }}>
                                {//@ts-ignore
                                    window.ReactNativeWebView && <FormControlLabel
                                        label={t('AutofillCredentials')}
                                        sx={{
                                            color: '#404040',
                                            '& .MuiTypography-root': {
                                                fontSize: 14,
                                                fontWeight: 'bold',
                                            },
                                        }}
                                        control={
                                            <Checkbox
                                                checked={rememberCredentials}
                                                onChange={(e) => {
                                                    setRememberCredentials(e.target.checked);
                                                    localStorage.setItem('remember_credentials', e.target.checked.toString());
                                                }}
                                            />
                                        }
                                    />}
                                <Button
                                    sx={{ boxShadow: 3, fontWeight: 'bold', mb: 1 }}
                                    variant="contained"
                                    fullWidth
                                    disabled={!code.trim() || !pin.trim() || !domain.trim()}
                                    onClick={handleLogin}
                                >
                                    {t('login.sign')}
                                </Button>
                                {!isIOS && <Stack direction={'row'} spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: 14 }}>{t('NoAccount')}</Typography>
                                    <Typography
                                        onClick={() => {
                                            setCurrentStep('Welcome');
                                        }}
                                        sx={{ fontSize: 16, fontWeight: 'bold', color: '#FFC600' }}>{t('Register')}</Typography>
                                </Stack>}
                                <LanguagePicker notUseRouteLanguage showText={false} onLanguageChange={(e) => {
                                    navigate('/' + e + location.pathname.substring(location.pathname.indexOf('/', 1)));
                                }} />
                            </Box>
                        </Box>
                        }
                        {currentStep === 'Welcome' && (
                            <Box sx={{ p: 4 }}>
                                <Typography sx={{ fontWeight: 'bold' }}>{t('Welcome')}!</Typography>
                                <Typography component="span">
                                    {t('CommunicateWithAnyoneWithinYourCompany,ManageItemsInYourWarehousesAndSeeYourEmployeesAttendanceAtOnce.ForMoreDetails')}{' '}
                                </Typography>
                                <Typography
                                    onClick={() => window.open('https://outlook.office365.com/owa/calendar/Stavario1@stavario.com/bookings/', '_blank')}
                                    component="span" sx={{ fontWeight: 'bold', color: '#FFC600', textDecoration: 'underline' }}>
                                    {t('BookProductPresentationOnline')}
                                </Typography>
                                <Typography sx={{ fontWeight: 'bold' }}>

                                </Typography>
                                <Stack direction={'row'} spacing={1} sx={{ mt: 2 }}>
                                    <MailOutlineIcon />
                                    <Typography
                                        sx={{ color: '#FFC600', fontWeight: 'bold', textDecoration: 'underline' }}
                                        onClick={() => {
                                            window.location.href = 'mailto:support@stavario.zendesk.com';
                                        }}
                                    >{'support@stavario.zendesk.com'}</Typography>
                                </Stack>
                                <Stack direction={'row'} spacing={1} sx={{ mt: 2 }}>
                                    <LanguageIcon />
                                    <Typography
                                        onClick={() => window.open('https://stavario.com', '_blank')}
                                        component="span" sx={{ fontWeight: 'bold', color: '#FFC600', textDecoration: 'underline' }}>
                                        {'stavario.com'}
                                    </Typography>
                                </Stack>

                            </Box>
                        )}
                        {currentStep === 'Partial' &&
                            <Box display='flex' alignItems={'center'} flexDirection='column' sx={{ width: '100%', pt: 4, pb: 3, gap: 4 }}>
                                <TextField
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    size='small'
                                    fullWidth
                                    label={t('CompanyName')}
                                ></TextField>
                                <TextField
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    size='small'
                                    fullWidth
                                    label={t('Email')}
                                ></TextField>
                            </Box>}
                        {currentStep === 'Complete' &&
                            <Box display='flex' alignItems={'center'} flexDirection='column' sx={{ width: '100%', gap: 4, pt: 4, pb: 3 }}>
                                <PhoneInput
                                    country={'cz'}
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    enableSearch
                                    disableSearchIcon
                                    searchPlaceholder={t('Search...')}
                                    searchNotFound={t('NoCountryFound')}
                                    inputProps={{
                                        name: 'phone',
                                        required: true,
                                        placeholder: t('PhoneNumber'),
                                    }}
                                    inputStyle={{
                                        marginLeft: 2,
                                        marginRight: 2,
                                        width: '100%',
                                        border: '1.5px solid #D0D0D0', borderRadius: '24px'
                                    }}
                                    containerStyle={{
                                        width: '100%',
                                    }}
                                    buttonStyle={{
                                        background: 'transparent', border: 'none',
                                        paddingLeft: '12px',
                                    }}

                                />
                                <TextField
                                    value={companyNumber}
                                    onChange={(e) => setCompanyNumber(e.target.value)}
                                    size='small'
                                    fullWidth
                                    label={t('CompanyNumber')}
                                ></TextField>
                                <FormControl sx={{ width: '100%' }} size="small">
                                    <InputLabel id="demo-multiple-checkbox-label">{t('ChooseYourModules')}</InputLabel>
                                    <Select
                                        labelId="demo-multiple-checkbox-label"
                                        id="demo-multiple-checkbox"
                                        multiple
                                        value={selectedModules}
                                        onChange={(event) => setSelectedModules(event.target.value as string[])}
                                        input={<OutlinedInput label={t('ChooseYourModules')} />}
                                        renderValue={(selected) => selected.map((value) => t(value)).join(", ")}
                                        MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                                    >
                                        {modules.map((name) => (
                                            <MenuItem key={name} value={name}>
                                                <Checkbox checked={selectedModules.includes(name)} />
                                                <ListItemText primary={t(name)} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>}
                        {currentStep === 'Created' && <Box display='flex' alignItems={'center'} flexDirection='column' sx={{ width: '100%', pb: 3 }}>
                            <CheckCircleOutlineIcon sx={{ color: '#FFC600', width: 90, height: 90 }} />
                            <Typography sx={{ fontWeight: 'bold' }}>{t('DemoCreated')}</Typography>
                            <Typography>{t('CheckSMSAndEmail(IncludingSpam)')}</Typography>
                            <Box>
                                <Stack direction={'row'} spacing={1} sx={{ mt: 2, justifyContent: 'flex-start', width: '100%' }}>
                                    <CoPresentIcon />
                                    <Typography
                                        onClick={() => window.open('https://outlook.office365.com/owa/calendar/Stavario1@stavario.com/bookings/', '_blank')}
                                        component="span" sx={{ fontWeight: 'bold', color: '#FFC600', textDecoration: 'underline' }}>
                                        {t('BookOnlinePresentation')}
                                    </Typography>
                                </Stack>
                                <Stack direction={'row'} spacing={1} sx={{ mt: 2, justifyContent: 'flex-start', width: '100%' }}>
                                    <LanguageIcon />
                                    <Typography
                                        onClick={() => window.open('https://vimeo.com/stavario', '_blank')}
                                        component="span" sx={{ fontWeight: 'bold', color: '#FFC600', textDecoration: 'underline' }}>
                                        {t('Tutorials')}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Box>
                        }
                        {currentStep !== 'Sign' && <Stack spacing={1} direction={'row'} sx={{ px: 4, pb: 3 }}>
                            {currentStep !== 'Created' && <Button
                                sx={{ mt: 2, color: 'white', background: 'black', '&:hover': { background: 'black' } }}
                                onClick={() => {
                                    if (currentStep === 'Partial') {
                                        setCurrentStep('Welcome')
                                    } else if (currentStep === 'Complete') {
                                        setCurrentStep('Partial')
                                    } else {
                                        setCurrentStep('Sign');
                                    }
                                }}
                                variant="text"

                            >
                                {t('Back')}
                            </Button>
                            }
                            <Button
                                sx={{ mt: 2 }}
                                onClick={() => {
                                    if (currentStep === 'Welcome') {
                                        setCurrentStep('Partial')
                                    } else if (currentStep === 'Partial') {
                                        handlePartialRegistration();
                                    } else if (currentStep === 'Complete') {
                                        handleCompleteRegistration();
                                    } else if (currentStep === 'Created') {
                                        setCurrentStep('Sign');
                                    }
                                }
                                }
                                variant="contained"
                                color="primary"
                            >
                                {t('Continue')}
                            </Button>
                        </Stack>
                        }
                    </Box>

                </Card>
            </Box>
        </Box >
    );
}
